import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import { httpGet } from "../../services/commonService";
import urlConstant from "../../constants/urlConstants";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

const InfoCard = ({ fields, getValues }) => (
  <Card
    sx={(theme) => ({
      background: theme.palette.secondary.lightest,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 0,
      width: "100%",
      flexDirection: "column",
      height: "100%",
      padding: "20px",
    })}
  >
    <TableContainer
      sx={{ background: "transparent" }}
      component={Paper}
      elevation={0}
      className="two-table"
    >
      <Table aria-label={`sponsor ${fields[0].category} info table`}>
        <TableBody>
          {fields.map((field, index) => (
            <TableRow
              key={index}
              sx={{
                "&:last-child td, &:last-child th": {
                  border: 0,
                },
              }}
            >
              <TableCell className="label-data">{field.label}</TableCell>
              <TableCell align="right">{getValues(field.key)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

function SponsorInfo() {
  const authenticationState = useContext(AuthenticationContext);
  const { t } = useTranslation();

  const methods = useForm({
    mode: "all",
    defaultValues: {
      isDisplay: false,
      isNoSponsorDivDisplay: false,
      isFetching: false,
      firstName: "",
      lastName: "",
      email: "",
      phoneNo: "",
      mobileNo: "",
      sponsorId: 0,
    },
  });

  const { setValue, getValues } = methods;

  const sponsorInfoCards = [
    {
      category: "personal",
      fields: [
        { key: "sponsorId", label: t("SponsorInfo.Sponsor ID") },
        { key: "firstName", label: t("SponsorInfo.First Name") },
        { key: "lastName", label: t("SponsorInfo.Last Name") },
      ],
    },
    {
      category: "contact",
      fields: [
        { key: "email", label: t("SponsorInfo.Email") },
        { key: "phoneNo", label: t("SponsorInfo.Phone") },
        { key: "mobileNo", label: t("SponsorInfo.Mobile No") },
      ],
    },
  ];

  useEffect(() => {
    httpGet(
      urlConstant.Sponsor.GetSponsorInformation,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        const responseData = response.Value.Data;

        if (responseData.RefID === 0) {
          setValue("isNoSponsorDivDisplay", true);
        } else {
          setValue("email", responseData.SponsorEmail || "-");
          setValue("firstName", responseData.SponsorFirstName || "-");
          setValue("lastName", responseData.SponsorLastName || "-");
          setValue("sponsorId", responseData.RefID || "-");
          setValue("phoneNo", responseData.SponsorPhone || "-");
          setValue("mobileNo", responseData.SponsorMobileNo || "-");
          setValue("isDisplay", true);
        }
      })
      .catch((err) => {
        console.error(err);
        setValue("isNoSponsorDivDisplay", true);
      });
  }, [
    authenticationState.accessToken,
    authenticationState.impersonateEmail,
    setValue,
  ]);

  return (
    <>
      <Box flexDirection="column" gap={2} display="flex">
        <Typography
          color="secondary.light"
          display={{ xs: "block", md: "none" }}
          variant="h4"
          fontSize={"24px"}
          fontWeight={600}
        >
          {t("My Team")}
        </Typography>
        <Box width="100%">
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              border: "1px solid var(--headerbg)",
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Typography
              color="secondary.light"
              variant="h4"
              mb={"20px"}
              fontWeight={600}
            >
              {t("SponsorInfo.My Sponsor Information")}
            </Typography>
            {getValues("isNoSponsorDivDisplay") ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="secondary.text" variant="body1">
                  {t("SponsorInfo.NoSponsorFound")}
                </Typography>
              </Box>
            ) : (
              <Stack gap="20px">
                <Grid container spacing={2}>
                  {sponsorInfoCards.map((cardData, index) => (
                    <Grid key={index} size={{ xs: 12, md: 6 }}>
                      <InfoCard
                        fields={cardData.fields}
                        getValues={getValues}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            )}
          </Card>
        </Box>
      </Box>
    </>
  );
}

export default SponsorInfo;
